import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import { parseTableParams } from '../../utils/Utils';
import { dateFilterColumn } from '../../utils/dateFilterColumn';
import { buildReportLabels } from '../../utils/reportLabels';
import DataTable from '../../components/ui/DataTable';
import PersonCell from '../../components/ui/PersonCell';
import ReportExportButtons from '../../components/ui/ReportExportButtons';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

// Kayda geçen ret nedenleri (AccessScanDataAccess) + eski kayıtların iki kodu. Süzgeç KOD gönderir (reason[eq]).
export const UNAUTHORIZED_REASON_CODES = [
  'access.noPermission',
  'access.outsideAllowedTime',
  'access.permissionExpired',
  'access.onLeave',
  'access.outOfRange',
  'access.mockLocation',
  'access.noPointLocation',
  'noPermission',
  'locationMismatch',
];

export const UNAUTHORIZED_EXPORT_URL = '/v1/unauthorized-access-attempts/export';

/**
 * AKM POC §7.5/§7.6 — Yetkisiz Erişimler: ad / kapı / tarih-saat / neden süzgeçleri (uç adlarıyla), bina ve okuyucu
 * sütunları, sunucu tarafı Excel / PDF (TAM veri, o anki süzgeçler).
 */
export default function UnauthorizedAccessPage() {
  const { t, i18n } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get('/v1/unauthorized-access-attempts', { params: paramsToUse })
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams]);

  const reasonLabel = useCallback((val) => {
    const map = {
      noPermission: t('unauthorizedAccess.noPermission'),
      locationMismatch: t('unauthorizedAccess.locationMismatch'),
    };
    // access.* sebepleri responseErrors ailesinden çevrilir (api.js deseni); eşleşme yoksa ham değer görünür.
    return map[val] || t(`responseErrors.${val}`, { defaultValue: val });
  }, [t]);

  // Sütunlar handleDataStateChange'ten ÖNCE: parseTableParams süzgeç adlarını bu listeden okur.
  const baseColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'userFullName', label: t('unauthorizedAccess.userFullName'),
      options: {
        filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (v, tableMeta) => (
          <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.userProfilePicture} />
        ),
      },
    },
    {
      name: 'buildingName', label: t('unauthorizedAccess.building'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'gateName', label: t('unauthorizedAccess.gateName'),
      options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'accessPointName', label: t('unauthorizedAccess.accessPoint'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    dateFilterColumn('timestamp', t('unauthorizedAccess.timestamp'), i18n.language, { includeTime: true, sort: false }),
    {
      name: 'reason', label: t('unauthorizedAccess.reason'),
      options: {
        filter: true, sort: false, filterType: 'dropdown',
        filterOptions: {
          names: UNAUTHORIZED_REASON_CODES,
          defaultOp: 'eq',
          renderValue: (v) => reasonLabel(v),
        },
        customFilterListOptions: { render: (v) => `${t('unauthorizedAccess.reason')}: ${reasonLabel(v)}` },
        customBodyRender: (value) => reasonLabel(value),
      },
    },
  ], [t, i18n.language, reasonLabel, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const getExportLabels = useCallback(() => {
    const reasons = {};
    UNAUTHORIZED_REASON_CODES.forEach((code) => { reasons[`reason.${code}`] = reasonLabel(code); });
    return buildReportLabels(t, baseColumns, t('unauthorizedAccess.title'), reasons);
  }, [t, baseColumns, reasonLabel]);

  const toolbarActions = useMemo(() => (
    <ReportExportButtons url={UNAUTHORIZED_EXPORT_URL} params={queryParams} getLabels={getExportLabels} />
  ), [queryParams, getExportLabels]);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('unauthorizedAccess.title')}
          fetching={fetching}
          columns={baseColumns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          toolbarActions={toolbarActions}
          download={false}
        />
      ), [
        t, fetching, baseColumns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange, toolbarActions,
      ])}
    </Container>
  );
}
